import { SetupInstance } from '../../app/msal-account-helpers';
import { protectedResources } from '../../authConfig';
import { InteractionRequiredAuthError, InteractionStatus } from '@azure/msal-browser';
import { hydrateObjects, cleanUpJsonForTransport } from '../json-helper';
import polly from 'polly-js';
import { AccessTokenHelper } from '../access-token-helper';
import { DateTime } from 'luxon';
import { msalConfig } from '../../app';
import { useLocal } from './../environment';

export class CalendarApi {
	constructor(instance, inProgress, accounts, forceRefresh) {

		this.instance = SetupInstance(instance);
		this.inProgress = inProgress;
		this.account = accounts && accounts[0];

		this.accessTokenRequest = {
			scopes: [
				...protectedResources.apiCalendar.scopes.read,
				...protectedResources.apiCalendar.scopes.write
			],
			authority: `https://login.microsoftonline.com/${msalConfig.auth.tenantId}/`,
			forceRefresh: forceRefresh,
			account: this.account,
		};

        this.baseUrl = useLocal ?
                    "https://property.manager.internal.gateway/calendar/api/calendar" :
                    "https://apim.ianrobertson.co.uk/api/calendar/v1";

		this.accessTokenService = new AccessTokenHelper();
	}

	static events = [];

	#getEventsFromCache = () => {
		return CalendarApi.events || [];
	};

	#getEventFromCache = id => {
		const events = this.#getEventsFromCache();
		return events.find(e => {
			if (!e) {
				return false;
			}
			if (e.id === id) {
				return true;
			}
			const extended = e.extendedProps || {};
			return extended.calendarEventId === id;
		});
	};

	#addEventToCache = event => {
		// Guard against accidentally passing arrays into the cache.
		if (!event || Array.isArray(event)) {
			return;
		}

		const events = this.#getEventsFromCache();

		// Use the underlying calendar event id (series id) for cache identity
		// when available, otherwise fall back to the instance id.
		const getCacheKey = e => {
			if (!e) {
				return null;
			}
			const extended = e.extendedProps || {};
			return extended.calendarEventId || e.id;
		};

		const key = getCacheKey(event);
		const extended = event.extendedProps || {};
		const isException = !!extended.isException;

		// If we don't already have any instances for this series, just add it.
		const hasExistingForSeries = events.some(e => getCacheKey(e) === key);
		if (!hasExistingForSeries) {
			CalendarApi.events.push(event);
			return;
		}

		// If this is a single-occurrence exception edit, only update the
		// matching instance (by id) and leave the rest of the series intact.
		if (isException) {
			const indexById = events.findIndex(e => e && e.id === event.id);
			if (indexById === -1) {
				CalendarApi.events.push(event);
			}
			else {
				CalendarApi.events.splice(indexById, 1, event);
			}
			return;
		}

		// Otherwise, this is a series-level update: update every existing
		// instance in the cache that belongs to this series, keeping each
		// instance's own start/end but refreshing the shared metadata.
		CalendarApi.events = events.map(e => {
			if (getCacheKey(e) !== key) {
				return e;
			}

			return Object.assign({}, event, {
				start: e.start,
				end: e.end
			});
		});
	};

	#addEventsToCache = events => {
		if (!events) {
			return;
		}

		// Accept a single event or an array of events. Also flatten a
		// single level if an array element itself is an array.
		const source = Array.isArray(events) ? events : [events];
		for (const item of source) {
			if (!item) {
				continue;
			}
			if (Array.isArray(item)) {
				for (const inner of item) {
					this.#addEventToCache(inner);
				}
			}
			else {
				this.#addEventToCache(item);
			}
		}
	};

	#removeEventFromCache = idOrSeriesKey => {
		if (!idOrSeriesKey) {
			return;
		}

		const events = this.#getEventsFromCache();
		CalendarApi.events = events.filter(e => {
			if (!e) {
				return false;
			}
			const extended = e.extendedProps || {};
			// Remove events that match either the instance id or the
			// underlying series id (calendarEventId).
			return e.id !== idOrSeriesKey && extended.calendarEventId !== idOrSeriesKey;
		});
	};

	fixStartAndEndOnAllDayEvents = (items) => {

		const result = items.map(ci => {
			if (ci && ci.allDay) {
				ci.end = this.setDateToLastSecondOfDay(ci.end ?? ci.start);
				ci.start = this.setDateToBeginningOfDay(ci.start);
			}
			return ci;
		});

		return result;
	};

	setDateToLastSecondOfDay = (date) => {

		const workingDate = typeof date === 'string' ? DateTime.fromISO(date, { zone: 'utc' }) : date;
		const result = DateTime.utc().set({
			year: workingDate.c.year,
			month: workingDate.c.month,
			day: workingDate.c.day,
			hour: 23,
			minute: 59,
			second: 59,
			millisecond: 999
		}).toISO();

		return result;
	};

	setDateToBeginningOfDay = (date) => {

		const workingDate = typeof date === 'string' ? DateTime.fromISO(date, { zone: 'utc' }) : date;
		const result = DateTime.utc().set({
			year: workingDate.c.year,
			month: workingDate.c.month,
			day: workingDate.c.day,
			hour: 0,
			minute: 0,
			second: 0,
			millisecond: 0
		}).toISO();

		return result;
	};

	#normaliseEventForTransport = (event) => {
		if (!event) {
			return event;
		}

		const payload = Object.assign({}, event);

		if (payload.start) {
			if (payload.start._isAMomentObject && typeof payload.start.toISOString === 'function') {
				payload.start = payload.start.toISOString();
			}
			else if (payload.start instanceof Date) {
				payload.start = payload.start.toISOString();
			}
		}

		if (payload.end) {
			if (payload.end._isAMomentObject && typeof payload.end.toISOString === 'function') {
				payload.end = payload.end.toISOString();
			}
			else if (payload.end instanceof Date) {
				payload.end = payload.end.toISOString();
			}
		}

		return payload;
	};

	getEvents = (from, to) => {

		return polly()
			.logger(error => {
				console.error("An error occurred in getEvents", error);
			})
			.waitAndRetry(5)
			.executeForPromise(() => {
				return (async () => {

					try {
						if (this.inProgress !== InteractionStatus.None) {
							await this.instance.acquireTokenSilent(this.accessTokenRequest);
							return;
						}

						const accessTokenResponse = await this.accessTokenService.getAccessToken(this.instance, this.inProgress, this.accessTokenRequest);

						if (!!!accessTokenResponse) {
							throw new Error("No access token");
						}

						const headers = new Headers();
						const bearer = `Bearer ${accessTokenResponse.accessToken}`;

						headers.append("Authorization", bearer);
						headers.append("Content-Type", "application/json");

						const options = {
							method: "GET",
							headers: headers
						};

						let url = `${this.baseUrl}/events`;
						const params = [];
						const formatDate = (value) => {
							if (!value) {
								return null;
							}
							if (typeof value === "string") {
								return value;
							}
							if (value instanceof Date && typeof value.toISOString === "function") {
								return value.toISOString();
							}
							return value.toString();
						};

						const fromValue = formatDate(from);
						const toValue = formatDate(to);

						if (fromValue) {
							params.push(`from=${encodeURIComponent(fromValue)}`);
						}
						if (toValue) {
							params.push(`to=${encodeURIComponent(toValue)}`);
						}

						if (params.length > 0) {
							url = `${url}?${params.join("&")}`;
						}

						const response = await fetch(url, options);
						if (!response.ok) {
							throw response;
						}

						const data = await response.json();
						const result = cleanUpJsonForTransport(Object.assign([], hydrateObjects(data)));
						const items = this.fixStartAndEndOnAllDayEvents(result || []);
						this.#addEventsToCache(items);
						return items;
					}
					catch (error) {
						if (error instanceof InteractionRequiredAuthError) {
							// Surface auth errors to the caller; global auth gate is
							// responsible for handling redirects.
						}
						console.log(error);
						throw error;
					}
				})();
			});
	};

	getEventById = id => {

		return polly()
			.logger(error => {
				console.error("An error occurred in getEventById", error);
			})
			.waitAndRetry(5)
			.executeForPromise(() => {
				return (async () => {
					try {
						// Try cache first to avoid unnecessary HTTP calls
						const cached = this.#getEventFromCache(id);
						if (cached) {
							return cached;
						}

						if (this.inProgress !== InteractionStatus.None) {
							await this.instance.acquireTokenSilent(this.accessTokenRequest);
							return;
						}

						const accessTokenResponse = await this.accessTokenService.getAccessToken(this.instance, this.inProgress, this.accessTokenRequest);

						if (!!!accessTokenResponse) {
							throw new Error("No access token");
						}

						const headers = new Headers();
						const bearer = `Bearer ${accessTokenResponse.accessToken}`;

						headers.append("Authorization", bearer);
						headers.append("Content-Type", "application/json");

						const options = {
							method: "GET",
							headers: headers
						};

						const response = await fetch(`${this.baseUrl}/${id}`, options);
						if (!response.ok) {
							throw response;
						}

						const data = await response.json();
						const result = cleanUpJsonForTransport(hydrateObjects(data));
						const fixed = this.fixStartAndEndOnAllDayEvents([result])[0];
						this.#addEventsToCache(fixed);
						return fixed;
					}
					catch (error) {
						if (error instanceof InteractionRequiredAuthError) {
							// Surface auth errors to the caller; global auth gate is
							// responsible for handling redirects.
						}
						console.log(error);
						throw error;
					}
				})();
			});
	};

	deleteEvent = (eventOrId) => {

		const isObject = typeof eventOrId === 'object' && eventOrId !== null;
		const id = isObject ? eventOrId.id : eventOrId;
		const extended = isObject && eventOrId.extendedProps ? eventOrId.extendedProps : {};
		const companyId = extended.companyId || (isObject ? eventOrId.companyId : "") || "";
		const calendarEventId = extended.calendarEventId || id || "";

		return polly()
			.logger(error => {
				console.error("An error occurred in deleteEvent", error);
			})
			.waitAndRetry(5)
			.executeForPromise(() => {
				return (async () => {

					try {
						if (this.inProgress !== InteractionStatus.None) {
							await this.instance.acquireTokenSilent(this.accessTokenRequest);
							return;
						}

						const accessTokenResponse = await this.accessTokenService.getAccessToken(this.instance, this.inProgress, this.accessTokenRequest);

						if (!!!accessTokenResponse) {
							throw new Error("No access token");
						}

						const headers = new Headers();
						const bearer = `Bearer ${accessTokenResponse.accessToken}`;

						headers.append("Authorization", bearer);
						headers.append("Content-Type", "application/json");

						const options = {
							method: "DELETE",
							headers: headers
						};

						let url;
						if (companyId && calendarEventId) {
							url = `${this.baseUrl}/events/${companyId}/${calendarEventId}`;
						}
						else {
							// Fallback to legacy single-parameter route if we
							// don't have enough context.
							url = `${this.baseUrl}/${id}`;
						}

						const response = await fetch(url, options);
						if (!response.ok) {
							throw response;
						}

						this.#removeEventFromCache(calendarEventId || id);

						return true;
					}
					catch (error) {
						if (error instanceof InteractionRequiredAuthError) {
							// Surface auth errors to the caller; global auth gate is
							// responsible for handling redirects.
						}
						console.log(error);
						throw error;
					}
				})();
			});
	};

	cancelOccurrence = (eventOrId) => {

		const isObject = typeof eventOrId === 'object' && eventOrId !== null;
		const id = isObject ? eventOrId.id : eventOrId;
		const extended = isObject && eventOrId.extendedProps ? eventOrId.extendedProps : {};
		const companyId = extended.companyId || (isObject ? eventOrId.companyId : "") || "";
		const calendarEventId = extended.calendarEventId || id || "";

		// Build the minimal payload expected by the backend cancel-occurrence
		// endpoint: a DTO with the clicked occurrence's UTC start time.
		let occurrenceStartUtc = null;
		if (isObject && eventOrId && eventOrId.start) {
			const start = eventOrId.start;
			let jsDate;
			if (start instanceof Date) {
				jsDate = start;
			} else if (start._isAMomentObject && typeof start.toDate === 'function') {
				jsDate = start.toDate();
			} else {
				jsDate = new Date(start);
			}
			occurrenceStartUtc = jsDate.toISOString();
		}
		const payload = {
			occurrenceStartUtc: occurrenceStartUtc
		};

		return polly()
			.logger(error => {
				console.error("An error occurred in cancelOccurrence", error);
			})
			.waitAndRetry(5)
			.executeForPromise(() => {
				return (async () => {

					try {
						if (this.inProgress !== InteractionStatus.None) {
							await this.instance.acquireTokenSilent(this.accessTokenRequest);
							return;
						}

						const accessTokenResponse = await this.accessTokenService.getAccessToken(this.instance, this.inProgress, this.accessTokenRequest);

						if (!!!accessTokenResponse) {
							throw new Error("No access token");
						}

						const headers = new Headers();
						const bearer = `Bearer ${accessTokenResponse.accessToken}`;

						headers.append("Authorization", bearer);
						headers.append("Content-Type", "application/json");

						const options = {
							method: "POST",
							headers: headers,
							body: JSON.stringify(payload)
						};

						let url;
						if (companyId && calendarEventId) {
							url = `${this.baseUrl}/events/${companyId}/${calendarEventId}/cancel-occurrence`;
						}
						else {
							// If we are missing context, fall back to a basic
							// route shape and let the API decide how to handle it.
							url = `${this.baseUrl}/events/${calendarEventId}/cancel-occurrence`;
						}

						const response = await fetch(url, options);
						if (!response.ok) {
							throw response;
						}

						// Remove only this specific instance from the local
						// cache; keep the rest of the series intact.
						if (id) {
							const events = this.#getEventsFromCache();
							CalendarApi.events = events.filter(e => !e || e.id !== id);
						}

						// The caller (calendar page) is responsible for trimming
						// the visible events collection; we return true to signal
						// success without relying on the response shape.
						return true;
					}
					catch (error) {
						if (error instanceof InteractionRequiredAuthError) {
							// Surface auth errors to the caller; global auth gate is
							// responsible for handling redirects.
						}
						console.log(error);
						throw error;
					}
				})();
			});
	};

	saveEvent = event => {

		// Normalise dates and build a CalendarEventDto-compatible payload
		// for both POST (create) and PUT (update) operations.
		const base = this.#normaliseEventForTransport(event);
		const method = !!base.id ? "PUT" : "POST";
		const extended = base.extendedProps || {};
		const companyId = extended.companyId || base.companyId || "";
		const calendarEventId = extended.calendarEventId || base.id || "";
		let url = this.baseUrl;

		// For existing events, call the UpdateCalendarEvent route:
		//   PUT events/{companyId}/{eventId}
		// companyId and eventId come from extendedProps / event id.
		if (method === "PUT") {
			url = `${this.baseUrl}/events/${companyId}/${calendarEventId}`;
		}
		else {
			url = `${this.baseUrl}/events`;
		}

		// Map UI recurrence fields (recurrenceDays/recurrenceEnd) into the
		// ExtendedProps recurrence fields expected by the API. When the UI
		// only updates the "repeat until" date (e.g. delete this-and-future),
		// preserve the existing recurrence rule (frequency/interval/byWeekDays)
		// instead of clearing it.
		const weekdayNumericMap = {
			Mon: 1,
			Tue: 2,
			Wed: 3,
			Thu: 4,
			Fri: 5,
			Sat: 6,
			Sun: 0 // DayOfWeek.Sunday = 0
		};

		// Prefer explicit UI selection (base.recurrenceDays) when present;
		// otherwise fall back to any existing numeric byWeekDays on the
		// extended props so we don't lose the recurrence pattern.
		let recurrenceDays = Array.isArray(base.recurrenceDays) ? base.recurrenceDays : null;
		let byWeekDays = [];
		if (recurrenceDays && recurrenceDays.length > 0) {
			byWeekDays = recurrenceDays
				.map(d => weekdayNumericMap[d])
				.filter(v => v !== undefined && v !== null);
		}
		else if (Array.isArray(extended.byWeekDays)) {
			byWeekDays = extended.byWeekDays.slice();
		}

		// For the end date, prefer an explicit recurrenceEnd from the UI;
		// otherwise keep any existing untilUtc from the extended props.
		let untilUtc = null;
		if (base.recurrenceEnd) {
			const endDate = DateTime.fromISO(base.recurrenceEnd, { zone: 'utc' }).endOf('day');
			untilUtc = endDate.toISO();
		}
		else if (extended.untilUtc) {
			untilUtc = extended.untilUtc;
		}

		const hasRecurrence =
			byWeekDays.length > 0 ||
			!!untilUtc ||
			!!extended.frequency ||
			!!extended.byMonthDay ||
			!!extended.count;

		const payload = {
			id: calendarEventId || "",
			allDay: !!base.allDay,
			title: base.title,
			description: base.description,
			location: base.location,
			start: base.start,
			end: base.end,
			color: base.color || "#00acac",
			extendedProps: Object.assign({}, extended, {
				companyId: companyId,
				calendarEventId: calendarEventId,
				// Recurrence info projected from the modal fields
				frequency: hasRecurrence ? (extended.frequency || "Weekly") : null,
				interval: hasRecurrence ? (extended.interval || 1) : null,
				byWeekDays: hasRecurrence ? (byWeekDays.length > 0 ? byWeekDays : null) : null,
				byMonthDay: hasRecurrence ? (extended.byMonthDay || null) : null,
				untilUtc: hasRecurrence ? (untilUtc || extended.untilUtc || null) : null,
				count: hasRecurrence ? (extended.count || null) : null
			})
		};

		return polly()
			.logger(error => {
				console.error("An error occurred in saveEvent", error);
			})
			.waitAndRetry(5)
			.executeForPromise(() => {
				return (async () => {

					try {
						if (this.inProgress !== InteractionStatus.None) {
							await this.instance.acquireTokenSilent(this.accessTokenRequest);
							return;
						}

						const accessTokenResponse = await this.accessTokenService.getAccessToken(this.instance, this.inProgress, this.accessTokenRequest);

						if (!!!accessTokenResponse) {
							throw new Error("No access token");
						}

						const headers = new Headers();
						const bearer = `Bearer ${accessTokenResponse.accessToken}`;

						headers.append("Authorization", bearer);
						headers.append("Content-Type", "application/json");

						const options = {
							method: method,
							headers: headers,
							body: JSON.stringify(cleanUpJsonForTransport(payload))
						};

						const response = await fetch(url, options);
						if (!response.ok) {
							throw response;
						}

						const data = await response.json();

						// saveEvent may return either a single event or an array of
						// events (e.g., all instances for a series). Handle both.
						if (Array.isArray(data)) {
							let hydrated = hydrateObjects(data);
							let hydratedArray;
							if (Array.isArray(hydrated)) {
								// Flatten one level in case the API returns nested arrays
								// such as [seriesSummary, [occurrences...]].
								const flat = [];
								for (const item of hydrated) {
									if (!item) continue;
									if (Array.isArray(item)) {
										flat.push(...item);
									}
									else {
										flat.push(item);
									}
								}
								hydratedArray = flat;
							}
							else if (hydrated) {
								hydratedArray = [hydrated];
							}
							else {
								hydratedArray = [];
							}

							const result = cleanUpJsonForTransport(Object.assign([], hydratedArray));
							const items = this.fixStartAndEndOnAllDayEvents(result || []);

							// Replace any existing cached events for this series with the
							// latest items from the API.
							if (items.length > 0) {
								const keyFrom = e => {
									if (!e) return null;
									const ext = e.extendedProps || {};
									return ext.calendarEventId || e.id;
								};

								const seriesKey = keyFrom(items[0]);
								if (seriesKey) {
									const existing = this.#getEventsFromCache();
									CalendarApi.events = existing.filter(e => keyFrom(e) !== seriesKey);
								}
							}

							this.#addEventsToCache(items);
							return items;
						}

						const cleaned = cleanUpJsonForTransport(Object.assign({}, hydrateObjects(data)));
						const [fixed] = this.fixStartAndEndOnAllDayEvents([cleaned]);
						this.#addEventsToCache(fixed);
						return fixed;
					}
					catch (error) {
						if (error instanceof InteractionRequiredAuthError) {
							// Surface auth errors to the caller; global auth gate is
							// responsible for handling redirects.
						}
						console.log(error);
						throw error;
					}
				})();
			});
	};
}

